import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { useToast } from '../ui/use-toast';
import AthleteAvatar from './AthleteAvatar';
import ChooseTurnDialog from '../trophy/ChooseTurnDialog';
import { competitionsService } from '../../services/competitions';
import { cn } from '../../lib/utils';
import type { EntryTurn } from '../../types/trophy';

/**
 * One entry belonging to this account: the viewer's own, or a child's.
 *
 * `userId` is null for the viewer, which is exactly what the withdrawal
 * endpoint wants (no `for_user_id`), so the two cases need no branch anywhere
 * below.
 */
export interface EnteredAthlete {
  /** The entry's id — unique per row, and what the list is keyed on. */
  entryId: string;
  /** Null for the viewer themselves. */
  userId: string | null;
  fullName: string;
  name?: string | null;
  surname?: string | null;
  avatarColor?: string | null;
  avatarUrl?: string | null;
  /** Null until the desk sorts them into a class. */
  categoryId?: string | null;
  categoryName?: string | null;
  /**
   * The turn booked, when the class is split over several bookable rounds.
   * Null puts them in every one of those rounds' boxes, as on the boards —
   * unless `turnToChoose`, which puts them in the box above the rounds.
   */
  roundId?: string | null;
  roundName?: string | null;
  /** The class is split over turns and none is booked yet. */
  turnToChoose?: boolean;
  /** The turns that can be booked or moved to from here, right now. */
  turns?: EntryTurn[];
  /** Null where the competition takes no fee. */
  paid?: boolean | null;
  /** Named in the question before withdrawing. */
  competitionName: string;
}

interface EnteredAthletesProps {
  entered: EnteredAthlete[];
  /** The stage they are entered in — what the withdrawal is addressed to. */
  competitionSlug: string;
  /** The list has changed on the server; the caller re-reads it. */
  onWithdrawn: () => void;
  className?: string;
}

/**
 * The people of this account who are on a start list, where they are looked
 * for: in the circuit's card for that stage, in the card of the round their
 * class runs in, and — before the desk has sorted them into a class — above
 * the rounds on the stage's own page, since no round is theirs yet.
 *
 * A managed account has no profile to visit, so without this a parent has no
 * sight of the entry they made anywhere except the picker that made it. Each
 * one can be taken back off, behind a question — and the question says what we
 * cannot: that money already paid is between them and the organiser, because
 * nothing in DivePeaks moves it.
 *
 * And where the class is split over turns, the turn is chosen here — or moved
 * to another — while the server offers any (`turns`: entries open, the booked
 * turn not started). See ChooseTurnDialog.
 */
export default function EnteredAthletes({
  entered,
  competitionSlug,
  onWithdrawn,
  className,
}: EnteredAthletesProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [withdrawing, setWithdrawing] = useState<EnteredAthlete | null>(null);
  const [saving, setSaving] = useState(false);
  const [choosingTurn, setChoosingTurn] = useState<EnteredAthlete | null>(null);

  if (entered.length === 0) return null;

  // Something to choose: a turn with a seat left that is not the one booked.
  // With every other turn full there is nowhere to go, so no button.
  const canChooseTurn = (athlete: EnteredAthlete) =>
    (athlete.turns ?? []).some((turn) => turn.id !== athlete.roundId && !turn.full);

  const withdraw = async () => {
    if (!withdrawing || saving) return;
    setSaving(true);
    try {
      await competitionsService.withdraw(competitionSlug, withdrawing.userId);
      toast({ title: t('guardian.entered.withdrawn', { name: withdrawing.fullName }) });
      setWithdrawing(null);
      onWithdrawn();
    } catch (error) {
      toast({
        title: t('guardian.entered.withdrawError'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cn('space-y-1.5', className)} data-testid="entered-athletes">
      {entered.map((athlete) => (
        <div key={athlete.entryId} className="flex items-center gap-2" data-testid="entered-athlete">
          <AthleteAvatar
            name={athlete.name ?? athlete.fullName}
            surname={athlete.surname}
            color={athlete.avatarColor}
            imageUrl={athlete.avatarUrl}
            size="sm"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{athlete.fullName}</p>
            <p className="truncate text-xs text-muted-foreground">
              {[
                athlete.userId ? null : t('guardian.picker.you'),
                athlete.categoryName,
                athlete.roundName,
                athlete.turnToChoose ? t('stageEntry.turnChoice.pending') : null,
                athlete.paid === false ? t('stageEntry.unpaid') : null,
              ]
                .filter(Boolean)
                .join(' · ') || t('guardian.entered.entered')}
            </p>
          </div>
          {canChooseTurn(athlete) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 shrink-0 px-2 text-xs"
              onClick={() => setChoosingTurn(athlete)}
              data-testid="choose-turn"
            >
              {t(athlete.roundId ? 'stageEntry.turnChoice.change' : 'stageEntry.turnChoice.choose')}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 shrink-0"
            aria-label={t('guardian.entered.withdraw', { name: athlete.fullName })}
            onClick={() => setWithdrawing(athlete)}
            data-testid="withdraw-athlete"
          >
            <X className="size-4 text-destructive" />
          </Button>
        </div>
      ))}

      <AlertDialog
        open={!!withdrawing}
        onOpenChange={(open) => !open && !saving && setWithdrawing(null)}
      >
        <AlertDialogContent className="w-[calc(100%-2rem)] grid-cols-[minmax(0,1fr)]">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('guardian.entered.confirmTitle', {
                name: withdrawing?.fullName ?? '',
                stage: withdrawing?.competitionName ?? '',
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('guardian.entered.confirmDescription', {
                name: withdrawing?.fullName ?? '',
                stage: withdrawing?.competitionName ?? '',
              })}
              {/* Said whether or not the desk has ticked "paid": the row only
                  says what the organiser has got round to, and a transfer made
                  outside the app is invisible to it. Not at a competition that
                  takes no fee, where there is nothing to refund. */}
              {withdrawing?.paid !== null && (
                <span className="mt-2 block">{t('guardian.entered.refundWarning')}</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={saving}
              onClick={(event) => {
                event.preventDefault();
                withdraw();
              }}
              data-testid="confirm-withdraw-athlete"
            >
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('guardian.entered.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ChooseTurnDialog
        athlete={choosingTurn && { ...choosingTurn, turns: choosingTurn.turns ?? [] }}
        competitionSlug={competitionSlug}
        onCancel={() => setChoosingTurn(null)}
        onBooked={() => {
          setChoosingTurn(null);
          onWithdrawn();
        }}
      />
    </div>
  );
}
